'use client';

import { LoggedInUserProps } from '@interfaces/auth';
import { USER_ROLE } from '@utils/auth/roles';
import { v2Links } from '@utils/navigation/links';
import { Avatar, AvatarFallback, AvatarImage } from '@v2/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuTrigger,
} from '@v2/ui/DropdownMenu';
import { HomeLine, Logout01 } from '@v2/ui/Icon/General';
import { Star01 } from '@v2/ui/Icon/Shapes';
import { User01, UserCheck01 } from '@v2/ui/Icon/Users';
import { Typography, typographyVariants } from '@v2/ui/Typography';
import { cn } from '@v2/utils/cn';
import { themeConfig } from '@v2/utils/tailwind-reference';
import { signOut } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';

export function UserNav({
  user,
  variant,
}: {
  user: LoggedInUserProps;
  variant: 'white' | 'black';
}) {
  const router = useRouter();
  const pathname = usePathname();
  const userFirstNameChar =
    user?.firstName && user?.firstName?.length > 0 ? user.firstName[0] : '';
  const userLastNameChar =
    user?.lastName && user?.lastName?.length > 0 ? user.lastName[0] : '';

  const isLandingpage = pathname === '/';
  const isPartnerslandingPage = pathname === '/sports-travel-agent-network';
  const userRole = user.role;
  const canAccessPartner = [USER_ROLE.agency, USER_ROLE.agencyMember].includes(
    userRole
  );
  const isIndivAgent = USER_ROLE.individualAgent === userRole;

  return (
    <div className="hidden md:block">
      <DropdownMenu>
        <DropdownMenuTrigger>
          <div className="flex flex-row items-center gap-x-3">
            <Typography
              variant="h4"
              size="tmd"
              className={cn(
                'font-semibold',
                variant === 'black' && 'text-gray-800',
                variant === 'white' && 'text-white'
              )}
            >
              {user.firstName}&nbsp;{user.lastName}
            </Typography>
            <Avatar
              className={cn(
                variant === 'white' && 'bg-[#98A2B329] backdrop-blur-sm',
                variant === 'black' && 'border border-gray-200',
                'h-12 w-12'
              )}
            >
              <AvatarImage src={user.imageUrl} className="object-cover" />
              <AvatarFallback
                className={typographyVariants({
                  size: 'tmd',
                  className: `font-semibold uppercase ${isLandingpage ? 'text-gray-400' : variant === 'black' ? 'text-gray-800' : 'text-white'}`,
                })}
              >
                {`${userFirstNameChar}${userLastNameChar}`}
              </AvatarFallback>
            </Avatar>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[194px] bg-white" align="end">
          <DropdownMenuSub>
            {isPartnerslandingPage && (
              <DropdownMenuItem
                className={cn('flex cursor-pointer items-center gap-x-2.5')}
                onSelect={() => router.push(v2Links.home)}
              >
                <HomeLine size="20" className="text-gray-500" />
                Homepage
              </DropdownMenuItem>
            )}
            {!canAccessPartner && !isIndivAgent && (
              <>
                <DropdownMenuItem
                  className={cn('flex cursor-pointer items-center gap-x-2.5')}
                  onSelect={() => router.push(v2Links.favorites)}
                >
                  <Star01
                    size="20"
                    pathProps={{ stroke: themeConfig.theme.colors.gray[500] }}
                  />
                  Favorites
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={cn('flex cursor-pointer items-center gap-x-2.5')}
                  onSelect={() => router.push(v2Links.profile)}
                >
                  <User01
                    size="20"
                    pathProps={{ stroke: themeConfig.theme.colors.gray[500] }}
                  />
                  Account
                </DropdownMenuItem>
              </>
            )}
            {canAccessPartner && (
              <DropdownMenuItem
                className={cn('flex cursor-pointer items-center gap-x-2.5')}
                onSelect={() => router.push(v2Links.partnersOverview)}
              >
                <UserCheck01
                  size="20"
                  pathProps={{ stroke: themeConfig.theme.colors.gray[500] }}
                />
                Partner
              </DropdownMenuItem>
            )}
            {isIndivAgent && (
              <DropdownMenuItem
                className={cn('flex cursor-pointer items-center gap-x-2.5')}
                onSelect={() => router.push(v2Links.affiliatePerformance)}
              >
                <UserCheck01
                  size="20"
                  pathProps={{ stroke: themeConfig.theme.colors.gray[500] }}
                />
                Performance
              </DropdownMenuItem>
            )}
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className={cn('flex cursor-pointer items-center gap-x-2.5')}
            onSelect={async () => {
              if (canAccessPartner || isIndivAgent) {
                await signOut({ callbackUrl: v2Links.partnersLanding });
              } else {
                await signOut({ callbackUrl: v2Links.home });
              }
            }}
          >
            <Logout01
              size="20"
              pathProps={{ stroke: themeConfig.theme.colors.gray[500] }}
            />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
